// ─── Notification feed + realtime (2026-07-30) ──────────────────────────────
// Admin + Confirmation Agent only. In-app; no browser/OS push.
//
// SINGLETON, ref-counted — deliberately not a plain per-component hook.
// Two components consume this (the app-level toast host and the header bell).
// The first version created a channel per hook instance, and because
// `supabase.channel(name)` returns the SAME instance for a given name, the second
// mount called `.on()` on an already-subscribed channel and threw
// "cannot add postgres_changes callbacks ... after subscribe()", crashing the
// admin into the ErrorBoundary. Sharing one subscription also means the bell and
// the toast can never disagree about the unread count, and we fetch once.
//
// Two delivery paths:
//   1. realtime INSERT on `notifications`, filtered to this user's own rows.
//   2. a fetch on init, on tab focus, on reconnect, and on every (re)SUBSCRIBED.
// (2) is load-bearing: `postgres_changes` has NO replay, so anything emitted
// while the socket was down is lost. Without it the bell would silently
// under-report. The unread count always comes from the server.
//
// ⚠️ THE ADMIN PAGES ONLY (owner, 2026-10-03). The feed follows the PAGE as well
// as the person: on a customer page — /preview, /editor, /upload, /imprimeur,
// /flow, the root — it opens no channel, fetches nothing and draws nothing, even
// with a staff session in that browser. It used to follow the session alone, so
// the owner opening a client's 3D preview in his own browser got a staff toast
// drawn over the book — one screenshot away from another client's name.
// Leaving the admin for such a page closes the feed at once (no remount grace).
// Guard: src/components/NotificationToasts.customerRoutes.test.tsx
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { notificationsApi, type AppNotification } from './api';
import { useAuthStore } from '@/store/useAuthStore';
import { usePermissions } from './permissionsStore';
import { onSignedOut } from './signOutCleanup';
import { isAdminPath } from './pwa/detect';

const FEED_LIMIT = 20;
/** Most toasts on screen at once. */
const MAX_TOASTS = 3;
/** Longer than the usual 3-5s: these carry an order ref worth reading. */
const TOAST_MS = 6000;

const toastTimers = new Map<string, ReturnType<typeof setTimeout>>();

interface Snapshot {
  items: AppNotification[];
  unread: number;
  /** Currently-visible toasts. OWNED HERE, not derived.
   *  The first design published an `incoming` array that the toast host had to
   *  consume into local state. That is a queue handed across a React boundary,
   *  and it lost the event every time: the host's local `queue` was dropped on
   *  any remount, and the consume/emit handshake raced with React batching.
   *  Measured symptom: rtEvents=1, guard passed, incoming cleared — and the host
   *  still rendered queueLen=0. Owning the list in the store removes the whole
   *  failure class: the host is now pure render + dismiss.
   *
   *  2026-08-13: this field WAS written here and never read. The host had been
   *  rewritten to re-derive its own list from `items` behind a local `useState`
   *  and a `Date.parse(created_at) >= mountedAt` watermark — see the note in
   *  NotificationToasts. That is why nothing ever popped. Both halves are wired
   *  together again, and the toast now rides the exact same store→render path
   *  the bell badge has always used successfully. */
  toasts: AppNotification[];
}

let state: Snapshot = { items: [], unread: 0, toasts: [] };
const listeners = new Set<(s: Snapshot) => void>();
const seen = new Set<string>();

let refCount = 0;
let channel: RealtimeChannel | null = null;
let activeUserId: string | null = null;
let firstLoadDone = false;

function emit(): void {
  for (const l of listeners) l(state);
}
function set(patch: Partial<Snapshot>): void {
  state = { ...state, ...patch };
  emit();
}

// ─── Toast queue ────────────────────────────────────────────────────────────
// Membership is decided ONLY by `seen` — an id set. Never by a clock.
// The previous host gated toasts on `Date.parse(created_at) >= mountedAt`, and
// realtime hands us the raw Postgres timestamptz text ("2026-08-13
// 01:02:39.350734+00" — space-separated, `+00`), which is outside the ES date
// format and parses at the engine's discretion. One NaN, or a client clock a
// few seconds fast, and every toast is silently dropped while the bell keeps
// working. An id set cannot fail that way.

function clearToastTimer(id: string): void {
  const t = toastTimers.get(id);
  if (t) { clearTimeout(t); toastTimers.delete(id); }
}

export function dismissToast(id: string): void {
  clearToastTimer(id);
  set({ toasts: state.toasts.filter((n) => n.id !== id) });
}

function scheduleToastDismiss(id: string): void {
  if (toastTimers.has(id)) return;
  toastTimers.set(id, setTimeout(() => dismissToast(id), TOAST_MS));
}

/** Hold a toast open while the pointer is over it. */
export function holdToast(id: string): void { clearToastTimer(id); }
export function resumeToast(id: string): void { scheduleToastDismiss(id); }

/**
 * Rows never surfaced before, newest first. Marks them seen, so the same row
 * arriving twice (realtime AND the next fetch) toasts once.
 */
function takeUnseen(rows: AppNotification[]): AppNotification[] {
  const out: AppNotification[] = [];
  for (const n of rows) {
    if (!n?.id || n.read_at || seen.has(n.id)) continue;
    seen.add(n.id);
    out.push(n);
  }
  return out;
}

/** The new visible stack with `rows` on top, each armed to auto-dismiss. */
function stackToasts(rows: AppNotification[]): AppNotification[] {
  for (const n of rows) scheduleToastDismiss(n.id);
  return [...rows, ...state.toasts].slice(0, MAX_TOASTS);
}

async function fetchFeed(): Promise<void> {
  try {
    const { notifications, unread } = await notificationsApi.list(FEED_LIMIT);
    // On the first load, register history as already-seen so opening the admin
    // does not fire a burst of toasts for things that happened yesterday.
    if (!firstLoadDone) {
      for (const n of notifications) seen.add(n.id);
      firstLoadDone = true;
      set({ items: notifications, unread });
      return;
    }
    // Every later fetch is also a DELIVERY PATH, not just a re-sync. Anything
    // unread we have never seen arrived while the socket was down or the tab was
    // hidden; postgres_changes has no replay, so this is its only chance to
    // surface. Consequence: the toast no longer depends on the websocket at all
    // — if the bell can count it, the toast can show it.
    const fresh = takeUnseen(notifications);
    set({
      items: notifications,
      unread,
      toasts: fresh.length > 0 ? stackToasts(fresh) : state.toasts,
    });
  } catch {
    // Offline / transient 5xx — keep what we have and retry on the next
    // focus/reconnect rather than blanking the bell.
  }
}

function onVisible(): void {
  if (document.visibilityState === 'visible') void fetchFeed();
}


/** Bumped by every stop(). A start() still waiting on getSession() when the feed
 *  is stopped (sign-out, or leaving the admin for a customer page) must not open
 *  its channel late — getSession() can hang for seconds on iOS Safari. */
let generation = 0;

async function start(userId: string): Promise<void> {
  const gen = generation;
  activeUserId = userId;
  void fetchFeed();

  window.addEventListener('visibilitychange', onVisible);
  window.addEventListener('online', onVisible);
  window.addEventListener('pageshow', onVisible);

  // Hand the USER'S access token to the realtime socket before subscribing.
  // `notifications` is RLS-protected, and Realtime evaluates those policies
  // against whatever token the socket authenticated with. If it is still the
  // anon key, the change feed is silently empty: no error, no events, and the
  // bell only ever updates via the fallback fetch — which is exactly what we
  // measured (unread climbed to 4 while `incoming` stayed 0).
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) supabase.realtime.setAuth(token);
  } catch {
    // Fall through: the fallback fetch still keeps the bell correct.
  }
  if (gen !== generation) return; // stopped while waiting: never subscribe late

  channel = supabase
    .channel(`notifications-${userId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${userId}` },
      (payload) => {
        const row = payload.new as AppNotification;
        const fresh = takeUnseen([row]);
        if (fresh.length === 0) return;
        set({
          items: [row, ...state.items].slice(0, FEED_LIMIT),
          unread: state.unread + 1,
          toasts: stackToasts(fresh),
        });
      },
    )
    .subscribe((status) => {
      // A (re)subscribe means the socket just came up — pull anything missed.
      if (status === 'SUBSCRIBED') void fetchFeed();
    });
}

// Teardown is DELAYED, never synchronous on the last unmount.
// React remounts subtrees routinely (a header swapping between the mobile and
// desktop bell, a route change, StrictMode). Tearing down the moment refCount
// hit 0 meant a transient remount ran stop(), which cleared `toasts` — so a
// toast that had just been pushed vanished before it could paint, while items
// and unread quietly repopulated from the restart fetch. That is precisely the
// symptom measured live: rtEvents=1, guard passed, toasts=0.
let teardownTimer: ReturnType<typeof setTimeout> | null = null;
const TEARDOWN_GRACE_MS = 10_000;

function cancelTeardown(): void {
  if (teardownTimer) { clearTimeout(teardownTimer); teardownTimer = null; }
}

function scheduleTeardown(): void {
  cancelTeardown();
  teardownTimer = setTimeout(() => {
    teardownTimer = null;
    if (refCount <= 0) stop();
  }, TEARDOWN_GRACE_MS);
}

function stop(): void {
  generation += 1;
  window.removeEventListener('visibilitychange', onVisible);
  window.removeEventListener('online', onVisible);
  window.removeEventListener('pageshow', onVisible);
  if (channel) { void supabase.removeChannel(channel); channel = null; }
  activeUserId = null;
  firstLoadDone = false;
  seen.clear();
  for (const t of toastTimers.values()) clearTimeout(t);
  toastTimers.clear();
  state = { items: [], unread: 0, toasts: [] };
}

/**
 * Sign-out: everything stops NOW — the channel, the focus/online refetches,
 * the toast timers — and every mounted consumer is handed the empty state.
 * The 10 s teardown grace above is for remounts, never for a person leaving:
 * during it the channel was still live and a focus still refetched the feed
 * (owner, 2026-10-01 — a notification after being logged out).
 */
export function resetNotifications(): void {
  cancelTeardown();
  stop();
  emit();
}
onSignedOut(resetNotifications);

export interface UseNotifications {
  items: AppNotification[];
  unread: number;
  /** Visible toasts, owned by the store. */
  toasts: AppNotification[];
  markRead: (ids: string[]) => void;
  markAllRead: () => void;
  /** False for anyone without `orders.view` — customers included — and false
   *  for everyone on a page outside /admin. */
  enabled: boolean;
  refresh: () => void;
}

export function useNotifications(): UseNotifications {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const { can } = usePermissions();
  const onAdminPage = isAdminPath(useLocation().pathname);
  // Same permission the worker enforces. A customer session has none, so nothing
  // here runs for them — and RLS would return no rows even if it did. And only
  // on the admin pages, whoever is signed in (see the note at the top).
  const enabled = !!userId && can('orders.view') && onAdminPage;

  const [snap, setSnap] = useState<Snapshot>(state);

  useEffect(() => {
    if (!onAdminPage) {
      // A customer page: nothing of the staff feed may outlive the admin page
      // just left — not for the 10 s remount grace either.
      resetNotifications();
      return;
    }
    if (!enabled || !userId) return;
    // If the signed-in user changed, tear the old subscription down first.
    if (activeUserId && activeUserId !== userId) { stop(); refCount = 0; }

    const listener = (s: Snapshot) => setSnap(s);
    listeners.add(listener);
    refCount += 1;
    cancelTeardown();
    // Only start if there is no live channel — a remount inside the grace window
    // reuses the existing subscription instead of restarting it. Either way this
    // consumer reads the store as it is NOW: its own snapshot may predate a
    // reset (a customer page in between), and old toasts must not flash back.
    if (!channel) void start(userId);
    setSnap(state);

    return () => {
      listeners.delete(listener);
      refCount -= 1;
      if (refCount <= 0) { refCount = 0; scheduleTeardown(); }
    };
  }, [enabled, userId, onAdminPage]);

  const markRead = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const now = new Date().toISOString();
    for (const id of ids) clearToastTimer(id);
    set({
      items: state.items.map((n) => (ids.includes(n.id) && !n.read_at ? { ...n, read_at: now } : n)),
      // A read notification has no business still popping at you.
      toasts: state.toasts.filter((n) => !ids.includes(n.id)),
    });
    notificationsApi.markRead({ ids })
      .then((r) => set({ unread: r.unread }))
      .catch(() => void fetchFeed());
  }, []);

  const markAllRead = useCallback(() => {
    const now = new Date().toISOString();
    for (const t of toastTimers.values()) clearTimeout(t);
    toastTimers.clear();
    set({
      items: state.items.map((n) => (n.read_at ? n : { ...n, read_at: now })),
      unread: 0,
      toasts: [],
    });
    notificationsApi.markRead({ all: true })
      .then((r) => set({ unread: r.unread }))
      .catch(() => void fetchFeed());
  }, []);

  return {
    items: snap.items,
    unread: snap.unread,
    toasts: snap.toasts,
    markRead,
    markAllRead,
    enabled,
    refresh: () => void fetchFeed(),
  };
}
